import React, { useEffect, useState } from 'react';
import { memberApi } from '../api';
import { ANALYSIS_KINDS } from '../prompts';
import { clinicBriefParagraphs, formatWhen } from '../clinicBrief';
import { useMemberApp } from '../AppProvider';
import { IconChart } from '../components/Icons';

function kindMeta(id) {
  return ANALYSIS_KINDS.find((kind) => kind.id === id) || { id, title: 'Clinic brief', blurb: '' };
}

export default function AnalysisPage() {
  const { isPro, setUpgradeOpen, conversations } = useMemberApp();
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isPro) return;
    memberApi.analyses().then((d) => setItems(d.analyses || [])).catch(() => {});
  }, [isPro]);

  const run = async (kind) => {
    if (!isPro) {
      setUpgradeOpen(true);
      return;
    }
    setBusy(kind);
    setError('');
    try {
      const created = await memberApi.createAnalysis({
        kind,
        conversation_ids: conversations
          .filter((c) => c.id && !String(c.id).startsWith('local_'))
          .slice(0, 6)
          .map((c) => c.id),
      });
      setItems((prev) => [created, ...prev]);
    } catch (e) {
      if (e.status === 402) setUpgradeOpen(true);
      else setError(e.message || 'Could not write that brief yet.');
    } finally {
      setBusy('');
    }
  };

  if (!isPro) {
    return (
      <div className="by-page">
        <div className="by-empty">
          <div className="glyph"><IconChart /></div>
          <h2>Analysis is a PRO feature</h2>
          <p>Turn your conversations into a short brief for the next oncology visit.</p>
          <button type="button" className="by-btn by-btn-dark" onClick={() => setUpgradeOpen(true)}>Upgrade to Pro</button>
        </div>
      </div>
    );
  }

  return (
    <div className="by-page">
      <div className="by-center">
        <h1>Analysis</h1>
        <p className="by-lede">
          A short clinic brief from your recent chats. Something you can take into the room and discuss with your care team.
        </p>
        {error ? <p className="by-fine" style={{ color: '#8a1f1f', marginBottom: 16 }}>{error}</p> : null}

        <div className="by-grid" style={{ marginBottom: 28 }}>
          {ANALYSIS_KINDS.map((kind) => (
            <button
              key={kind.id}
              type="button"
              className="by-prompt"
              disabled={!!busy}
              onClick={() => run(kind.id)}
            >
              <span>
                <b>{kind.title}</b>
                <small>{busy === kind.id ? 'Writing…' : kind.blurb}</small>
              </span>
            </button>
          ))}
        </div>

        {!items.length ? (
          <div className="by-empty">
            <div className="glyph"><IconChart /></div>
            <h2>No briefs yet</h2>
            <p>Choose a type above. Vani reads your recent conversations and writes what to take to clinic.</p>
          </div>
        ) : (
          <div className="by-list">
            {items.map((item) => {
              const meta = kindMeta(item.kind);
              const when = formatWhen(item.created_at);
              const paragraphs = clinicBriefParagraphs(item.summary);
              return (
                <article className="by-list-card by-brief" key={item.id}>
                  <header className="by-brief-head">
                    <h3>{meta.title}</h3>
                    {when ? <time dateTime={item.created_at}>{when}</time> : null}
                  </header>
                  <div className="by-brief-body">
                    {paragraphs.map((p, idx) => <p key={`${item.id}-${idx}`}>{p}</p>)}
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
